
import { useState } from "react";
import { useForm } from "react-hook-form";
import { PlusCircle, Upload, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  scriptContent: z.string().min(1, "Script content is required"),
  scriptType: z.enum(["shell", "python", "javascript"]),
});

type ConfigurationForm = z.infer<typeof formSchema>;

interface ConfigurationManagerProps {
  onSuccess?: () => void;
}

export function ConfigurationManager({ onSuccess }: ConfigurationManagerProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  
  const form = useForm<ConfigurationForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      scriptContent: "",
      scriptType: "shell",
    },
  });

  const onSubmit = async (data: ConfigurationForm) => {
    try {
      setIsSubmitting(true);
      console.log("Submitting form data:", data);

      // Check if user is authenticated
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      console.log("Session data:", session);
      if (sessionError) {
        console.error("Session error:", sessionError);
        toast.error("Authentication error. Please try logging in again.");
        return;
      }

      if (!session?.session?.user) {
        console.log("No authenticated user found");
        toast.error("You must be logged in to create scripts");
        return;
      }

      console.log("Authenticated user ID:", session.session.user.id);

      const { data: insertedData, error: insertError } = await supabase
        .from("configurations")
        .insert({
          name: data.name,
          description: data.description || null,
          script_content: data.scriptContent,
          script_type: data.scriptType,
          user_id: session.session.user.id,
        })
        .select()
        .single();

      if (insertError) {
        console.error("Supabase error:", insertError);
        toast.error(`Failed to create script: ${insertError.message}`);
        return;
      }

      console.log("Successfully inserted data:", insertedData);
      toast.success("Configuration created successfully");
      form.reset();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating configuration:", error);
      toast.error("Failed to create configuration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "text/x-sh" && !file.name.endsWith(".sh")) {
      toast.error("Please upload a valid shell script file (.sh)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      form.setValue("scriptContent", content);
    };
    reader.readAsText(file);
  };

  const generateScript = async (prompt: string) => {
    if (!prompt) {
      toast.error("Please enter a prompt for script generation");
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      console.log("Calling generate-script function with prompt:", prompt);
      
      const { data, error } = await supabase.functions.invoke('generate-script', {
        body: { prompt }
      });

      if (error) {
        console.error("Error calling generate-script function:", error);
        throw new Error(`Failed to call generate-script function: ${error.message}`);
      }

      console.log("Response from generate-script:", data);

      if (data?.scriptContent) {
        form.setValue("scriptContent", data.scriptContent);
        toast.success("Script generated successfully");
      } else {
        throw new Error("No script content received from the AI service");
      }
    } catch (error) {
      console.error("Error generating script:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to generate script";
      setGenerationError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    const prompt = window.prompt("Enter your prompt for script generation");
    if (prompt) {
      generateScript(prompt);
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Create New Script</DialogTitle>
      </DialogHeader>
      
      {generationError && (
        <Alert variant="destructive">
          <AlertDescription>
            {generationError}
          </AlertDescription>
        </Alert>
      )}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter configuration name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Enter configuration description"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="scriptType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Script Type</FormLabel>
                <Select 
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select script type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="shell">Bash Script (macOS)</SelectItem>
                    <SelectItem value="python">Python</SelectItem>
                    <SelectItem value="javascript">JavaScript</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('script-file')?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Script
              </Button>
              <input
                id="script-file"
                type="file"
                accept=".sh"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateClick}
                disabled={isGenerating}
              >
                <Wand2 className="mr-2 h-4 w-4" />
                {isGenerating ? "Generating..." : "Generate with AI"}
              </Button>
            </div>

            <FormField
              control={form.control}
              name="scriptContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Script Content</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter your script or use the options above"
                      className="font-mono"
                      rows={10}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Script"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
